/**
 * Authentication Schemas
 *
 * Zod schemas for validating authentication-related API responses.
 */

import { z } from 'zod';

// Organisation schema
export const OrganisationSchema = z.object({
  id: z.number(),
  name: z.string(),
  role: z.string(),
});

export type Organisation = z.infer<typeof OrganisationSchema>;

// User schema
export const UserSchema = z.object({
  id: z.number(),
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
  email_verified: z.boolean().optional(),
  organisations: z.array(OrganisationSchema).optional(),
  is_admin: z.boolean().optional(),
  job_title: z.string().optional(),
  needs_password_upgrade: z.boolean().optional(),
});

export type User = z.infer<typeof UserSchema>;

// Login request schema
export const LoginRequestSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

export type LoginRequest = z.infer<typeof LoginRequestSchema>;

// Login response schema
export const LoginResponseSchema = z.object({
  id: z.number(),
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
  job_title: z.string().optional(),
  email_verified: z.boolean().optional(),
  organisations: z.array(OrganisationSchema).optional(),
  is_admin: z.boolean().optional(),
  needs_password_upgrade: z.boolean().optional(),
  token: z.string().optional(), // JWT token if using token-based auth
  access_token: z.string().optional(), // JWT access token from simplejwt
  expires_at: z.string().optional(), // Token expiry time
});

export type LoginResponse = z.infer<typeof LoginResponseSchema>;

// Registration request schema
export const RegisterRequestSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string()
    .min(12, 'Password must be at least 12 characters')
    .regex(/[a-z]/, 'Password must include a lowercase letter')
    .regex(/[A-Z]/, 'Password must include an uppercase letter')
    .regex(/[0-9]/, 'Password must include a number')
    .regex(/[^a-zA-Z0-9]/, 'Password must include a symbol'),
  password_confirm: z.string(),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  organisation_name: z.string().min(1, 'Organisation name is required'),
  organisation_registered_country_id: z.number(),
}).refine((data) => data.password === data.password_confirm, {
  message: 'Passwords do not match',
  path: ['password_confirm'],
});

export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

// Registration response schema
export const RegisterResponseSchema = z.object({
  id: z.number(),
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
  job_title: z.string().optional(),
  email_verified: z.boolean().optional(),
  organisations: z.array(OrganisationSchema).optional(),
  organisation_id: z.number().optional(),
  organisation_name: z.string().optional(),
  invitation_accepted: z.boolean().optional(),
  is_admin: z.boolean().optional(),
  needs_password_upgrade: z.boolean().optional(),
  access_token: z.string().optional(),
  token: z.string().optional(),
});

export type RegisterResponse = z.infer<typeof RegisterResponseSchema>;

// Change password request schema
export const ChangePasswordRequestSchema = z.object({
  old_password: z.string().min(1, 'Current password is required'),
  new_password: z.string()
    .min(12, 'Password must be at least 12 characters')
    .regex(/[a-z]/, 'Password must include a lowercase letter')
    .regex(/[A-Z]/, 'Password must include an uppercase letter')
    .regex(/[0-9]/, 'Password must include a number')
    .regex(/[^a-zA-Z0-9]/, 'Password must include a symbol'),
  new_password_confirm: z.string(),
}).refine((data) => data.new_password === data.new_password_confirm, {
  message: 'Passwords do not match',
  path: ['new_password_confirm'],
});

export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;

// Profile schema
export const ProfileSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.string().email(),
  job_title: z.string().optional(),
});

export type Profile = z.infer<typeof ProfileSchema>;

// Session info schema
export const SessionInfoSchema = z.object({
  user: UserSchema,
  expiresAt: z.string(),
  issuedAt: z.string(),
});

export type SessionInfo = z.infer<typeof SessionInfoSchema>;
